import { JWT_PROPERTIES } from '@constants/auth.constants';
import { CognitoLoginResponse, CognitoAuthData } from './types';
import storageService from './storageService';

/**
 * Parse JWT token and extract payload
 * @param token - JWT token to parse
 * @returns Parsed payload or null if invalid
 */
export const parseJwtToken = (token: string | null): any | null => {
  if (!token) {
    console.error('No JWT provided');
    return null;
  }
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
};

/**
 * Check if token is expired based on expiration timestamp
 * @param targetDate - Expiration timestamp in seconds
 * @returns Boolean indicating if token is expired
 */
export const isTokenExpiredByTimestamp = (targetDate: number): boolean => {
  const targetDateTime = new Date(targetDate * 1000);
  const fixedDateTime = new Date();
  fixedDateTime.setMinutes(targetDateTime.getMinutes() - 2);
  const currentDate = new Date();

  return fixedDateTime > currentDate;
};

/**
 * Extracts groups from Cognito JWT token
 * @param token - JWT ID token from Cognito
 * @returns Array of group names
 */
export const extractGroupsFromToken = (token: string): string[] => {
  try {
    const payload = parseJwtToken(token);
    return payload?.[JWT_PROPERTIES.COGNITO_GROUPS] || [];
  } catch (error) {
    console.error('Error extracting groups from token:', error);
    return [];
  }
};

/**
 * Checks if token is expired
 * @param token - JWT token to check
 * @returns Boolean indicating if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = parseJwtToken(token);
    const exp = payload?.exp;

    if (!exp) return true;

    return isTokenExpiredByTimestamp(exp);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Processes Cognito login response and builds complete auth response
 * @param cognitoResponse - Response from Cognito login
 * @returns Complete Cognito auth response
 */
export const processLoginResponse = (
  cognitoResponse: CognitoLoginResponse
): CognitoAuthData => {
  const roles = extractGroupsFromToken(cognitoResponse.idToken);
  const permissions =
    cognitoResponse.permissions?.map((permission) => ({
      action: permission.action,
      subject: permission.subject,
    })) || [];

  storageService.setRoles(roles);
  storageService.setPermissions(permissions);
  storageService.setIdToken(cognitoResponse.idToken);
  storageService.setAccessToken(cognitoResponse.accessToken || '');
  storageService.setRefreshToken(cognitoResponse.refreshToken || '');

  return {
    ...cognitoResponse,
    roles,
  };
};

/**
 * Handles Cognito token refresh and updates roles
 * @param newIdToken - New ID token from refresh response
 * @returns Boolean indicating success
 */
export const handleTokenRefresh = (newIdToken: string): boolean => {
  try {
    if (!newIdToken || typeof newIdToken !== 'string') {
      console.error('Invalid ID token provided to handleTokenRefresh');
      return false;
    }

    if (isTokenExpired(newIdToken)) {
      console.error('Provided ID token is already expired');
      return false;
    }

    const roles = extractGroupsFromToken(newIdToken);
    storageService.setRoles(roles);

    return true;
  } catch (error) {
    console.error('Error handling token refresh:', error);
    return false;
  }
};

/**
 * Builds a default Cognito auth response when user profile is unavailable
 * @param cognitoResponse - Response from Cognito login
 * @returns Default Cognito auth response
 */
export const buildDefaultAuthResponse = (
  cognitoResponse: CognitoLoginResponse
): CognitoAuthData => processLoginResponse(cognitoResponse);

const cognitoService = {
  parseJwtToken,
  isTokenExpiredByTimestamp,
  extractGroupsFromToken,
  isTokenExpired,
  processLoginResponse,
  handleTokenRefresh,
  buildDefaultAuthResponse,
};

export default cognitoService;
